import {types, flow, getEnv} from 'mobx-state-tree';
import WithFeedbackModel, {WithFeedbackActions} from '../composables/withFeedbackModel';
import * as api from '../../services/api';
import normalizeVerificationMethods from './utils/normalizeVerificationMethods';
import {ListingImage} from '../ListingModel';
import normalizeListing from './utils/normalizeListing';
import {History} from 'history';

export enum AirbnbFrictionType {
  Email = 'email_code_verification',
  Phone = 'phone_verification_via_text'
}

export const AirbnbFriction = types.enumeration<AirbnbFrictionType>(
  Object.keys(AirbnbFrictionType).map(key => AirbnbFrictionType[key])
);

export const AirbnbCodeVerificationBase = types.model({
  airlockId: types.number,
  userId: types.number,
  hintText: types.string,
  codeRequested: types.optional(types.boolean, false),
  accessToken: types.string,
  isSelected: types.optional(types.boolean, false)
});

export const AirbnbEmailVerificationModel = types.compose(
  AirbnbCodeVerificationBase,
  types.model({
    friction: types.literal(AirbnbFrictionType.Email)
  })
);

export type AirbnbEmailVerification = typeof AirbnbEmailVerificationModel.Type;

export const AirbnbPhoneVerificationModel = types.compose(
  AirbnbCodeVerificationBase,
  types.model({
    friction: types.literal(AirbnbFrictionType.Phone),
    phoneId: types.number
  })
);

export type AirbnbPhoneVerification = typeof AirbnbPhoneVerificationModel.Type;

export const AirbnbRedirectVerificationModel = types.model({
  airlockId: types.number,
  linkRedirect: types.string,
  linkClicked: types.optional(types.boolean, false)
});

export type AirbnbRedirectVerification = typeof AirbnbRedirectVerificationModel.Type;

export const AirbnbVerificationMethodModel = types.union(
  AirbnbEmailVerificationModel,
  AirbnbPhoneVerificationModel,
  AirbnbRedirectVerificationModel
);

export type AirbnbCodeVerification = AirbnbEmailVerification | AirbnbPhoneVerification;
export type AirbnbVerificationMethod = AirbnbCodeVerification | AirbnbRedirectVerification;

export enum ExternalListingStatusType {
  EXISTS = 'EXISTED',
  AVAILABLE = 'AVAILABLE'
}

export const ExternalListingStatus = types.enumeration<ExternalListingStatusType>(
  Object.keys(ExternalListingStatusType).map(key => ExternalListingStatusType[key])
);

export const ExternalListingModel = types
  .model('ExternalListing', {
    otaListingId: types.identifier,
    name: types.string,
    address: types.maybe(types.string),
    status: ExternalListingStatus,
    timezoneName: types.maybe(types.string),
    countryCode: types.maybe(types.string),
    rooms: types.maybe(types.number),
    latitude: types.maybe(types.number),
    longitude: types.maybe(types.number),
    images: types.array(ListingImage)
  })
  .views(self => ({
    get previewImageUrl() {
      return self.images[0].get('small') || self.images[0].get('url');
    }
  }));

export type ExternalListingModelType = typeof ExternalListingModel.Type;

export type AirbnbAuthViews = {
  verifying: boolean;
  verificationProcessed: boolean;
  verificationPassed: boolean;
  verificationFailed: boolean;
};

export type AirbnbCodeVerificationCallbacks = {
  onSuccess?: () => void;
  onFailure?: () => void;
};

export type AirbnbAuthActions = {
  login: (email: string, password: string, onSuccess: () => void, onVerificationRequired: () => void) => void;
  requestVerificationCode: (method: AirbnbCodeVerification) => void;
  markRedirectLinkAsClicked: (method: AirbnbRedirectVerification) => void;
  finishRedirectVerification: (onSuccess: () => void) => void;
  verify: (method: AirbnbCodeVerification, code: string, callbacks?: AirbnbCodeVerificationCallbacks) => void;
  verified: (callback?: () => void) => void;
  failedVerification: (callback?: () => void) => void;
  useAnotherAccount: () => void;
  loadExernalListings: () => void;
  importExternalListings: (otaListingIds: (string | number)[]) => void;
};

const AirbnbAuthModel = types.compose(
  WithFeedbackModel,
  types
    .model('AirbnbAuth', {
      email: types.optional(types.string, ''),
      password: types.optional(types.string, ''),
      airbnbAccountId: types.maybe(types.union(types.string, types.number)),
      externalListings: types.array(ExternalListingModel),
      verificationRequired: types.optional(types.boolean, false),
      verificationMethods: types.array(AirbnbVerificationMethodModel),
      verificationStatus: types.optional(
        types.model({
          processed: types.boolean,
          processing: types.boolean,
          succeeded: types.boolean
        }),
        {processed: false, processing: false, succeeded: false}
      ),
      authenticating: types.optional(types.boolean, false),
      authenticated: types.optional(types.boolean, false),
      loadingExternalListings: types.optional(types.boolean, false),
      loadedExternalListings: types.optional(types.boolean, false),
      isImportingListings: types.optional(types.boolean, false)
    })
    .views<AirbnbAuthViews>(self => ({
      get verifying() {
        return self.verificationStatus.processing;
      },
      get verificationProcessed() {
        return self.verificationStatus.processed;
      },
      get verificationPassed() {
        return self.verificationStatus.processed && self.verificationStatus.succeeded;
      },
      get verificationFailed() {
        return self.verificationStatus.processed && !self.verificationStatus.succeeded;
      }
    }))
    .actions<AirbnbAuthActions>(self => {
      // tslint:disable-next-line:no-any
      const withFeedback = (self as any) as WithFeedbackActions;

      // tslint:disable-next-line:no-any
      const withActions = (self as any) as AirbnbAuthActions;

      const {router} = getEnv(self);

      return {
        login: async function(
          email: string,
          password: string,
          onSuccess: () => void,
          onVerificationRequired: () => void
        ) {
          const {status, data} = await api.loginToAirbnb(getEnv(self).teams.currentTeam.id, email, password);

          if (status !== 200 || !data) {
            return withFeedback.sendErrorFeedback(
              'Login failed. Please double check your credentials. ' +
                'If you are positive that you have entered the correct credentials, ' +
                'please contact us for further assistance.'
            );
          }

          self.email = email;
          self.password = password;

          if (!data.airlockId) {
            self.airbnbAccountId = data.accountId;
            return onSuccess();
          }

          self.verificationRequired = true;

          if (data.isCodeVerification || data.isRedirect) {
            // tslint:disable-next-line:no-any
            self.verificationMethods = normalizeVerificationMethods(data) as any;
            return onVerificationRequired();
          }
        },

        requestVerificationCode: async function (method: AirbnbCodeVerification) {
          const {status} = await api.requestAirbnbVerificationCode(method);

          if (status === 200) {
            method.isSelected = true;
          } else {
            withFeedback.sendErrorFeedback('An error has occurred. Please try again.');
          }
        },

        markRedirectLinkAsClicked: function(method: AirbnbRedirectVerification) {
          method.linkClicked = true;
        },

        finishRedirectVerification: async function(onSuccess: () => void) {
          const {email, password} = self;

          const response = await api.loginToAirbnb(getEnv(self).currentTeam.id, email, password);

          if (response.status === 200) {
            onSuccess();
          } else {
            withFeedback.sendErrorFeedback();
          }
        },

        verify: async function(
          method: AirbnbCodeVerification,
          code: string,
          {onSuccess, onFailure}: {onSuccess?: () => void; onFailure?: () => void}
        ) {
          self.verificationStatus.processing = true;

          const response = await api.verifyAirbnbCode(method, code.trim());

          if (response.status === 200) {
            withActions.verified(onSuccess);
          } else {
            withActions.failedVerification(onFailure);
          }
        },

        verified: function(callback?: () => void) {
          self.verificationStatus = {processed: true, processing: false, succeeded: true};

          if (typeof callback === 'function') {
            callback();
          }
        },

        failedVerification: function(callback?: () => void) {
          self.verificationStatus = {processed: true, processing: false, succeeded: false};

          if (typeof callback === 'function') {
            callback();
          }
        },

        useAnotherAccount: function() {
          self.email = '';
          self.password = '';
          self.verificationMethods.slice(0, self.verificationMethods.length);
        },

        loadExernalListings: flow(function*() {
          self.loadingExternalListings = true;
          const response = yield api.getExternalAirbnbListings(self.airbnbAccountId!);

          if (response && response.status === 200 && response.data) {
            // tslint:disable-next-line:no-any
            self.externalListings = response.data.listings.map((x: any) => normalizeListing(x));
            self.loadedExternalListings = true;
          } else {
            withFeedback.sendErrorFeedback();
          }

          self.loadingExternalListings = false;
        }),

        importExternalListings: flow(function*(otaListingIds: []) {
          self.isImportingListings = true;

          const response = yield api.chooseAirbnbListings(self.airbnbAccountId!, otaListingIds);

          if (response && response.status >= 200 && response.status < 300) {
            (router as History).push('/invite');
          } else {
            withFeedback.sendErrorFeedback();
          }

          self.isImportingListings = false;
        })
      };
    })
);

export type AirbnbAuth = typeof AirbnbAuthModel.Type;

export default AirbnbAuthModel;



// WEBPACK FOOTER //
// ./src/models/airbnbAuthModel/airbnbAuthModel.ts